import {
  Badge,
  ButtonList,
  DropdownButton,
  Icon,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useHasAccess } from 'lib-shared-access-control';
import type { PhoneNumber } from 'lib-shared-legacy-types';
import {
  formatInternational,
  getCountryCodeFromPhoneNumber,
} from 'lib-shared-phone-numbers';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import type { FlowCallActionT } from 'components/powerCallFlow/api/types';
import type { WithFlowsInjectedPropsT } from 'hocs';
import { withFlows } from 'hocs';
import { FlowT } from 'hocs/flows/api/types';
import { useNotifications } from 'hooks/useNotifications';

import { useNumberTable } from './numberTableContext';
import NumberTableFlows from './numberTableFlows';
import styles from './numberTableItem.module.scss';

type PropsT = {
  number: PhoneNumber;
} & WithFlowsInjectedPropsT<FlowCallActionT>;

function NumberTableItem({ number, flows }: PropsT) {
  const { showReleaseNumber, showChangeOutbound, showEditNumber, showNewFlow } =
    useNumberTable();
  const navigate = useNavigate();
  const notify = useNotifications();

  const hasChannelSetupAccess = useHasAccess(
    'settings.integration.channel.setup',
    {
      fallbackRole: 'admin',
    },
  );

  const {
    carrier,
    e164_format,
    incoming_functionality,
    name,
    number_type,
    organization_default,
    outbound_verified,
    outgoing_functionality,
    record_outbound_calls,
  } = number;

  const { items, paramsChange, getItems } = flows;

  const countryCode = getCountryCodeFromPhoneNumber(e164_format);
  const prettyNumber = formatInternational(e164_format);

  const handleFlowFetch = useCallback(
    () =>
      paramsChange({
        locations: e164_format,
        channel: 'pstn_phone',
      }).then(() => getItems()),
    [e164_format, paramsChange, getItems],
  );

  const onToggleMenu = useCallback((isOpen: boolean) => {
    if (isOpen) {
      ampli.phoneOptionsMenuOpened();
    }
  }, []);

  const onRelease = useCallback(() => {
    ampli.phoneOptionsMenuOptionRemoveNumberClicked();
    showReleaseNumber(number);
  }, [showReleaseNumber, number]);

  const onOutbound = useCallback(() => {
    ampli.phoneOptionsMenuOptionSetDefaultCallerIdClicked();
    showChangeOutbound(number);
  }, [showChangeOutbound, number]);

  const onEditFlow = useCallback(() => {
    ampli.phoneOptionsMenuOptionEditClicked();

    const redirectToActiveFlow = (flows: FlowT<FlowCallActionT>[]) => {
      const activeFlow = flows.find(({ active }) => active);

      if (!activeFlow) {
        notify({
          title: 'No active flow',
          text: `There's no active flow for number ${e164_format}`,
          icon: 'exclamation',
          className: 'danger',
        });

        return;
      }

      navigate(`/settings/phone/flow/${activeFlow.id}`);
    };

    if (items.length > 0) {
      redirectToActiveFlow(items);

      return;
    }

    handleFlowFetch().then(({ data: { flows } }) => {
      redirectToActiveFlow(flows || []);
    });
  }, [e164_format, navigate, items, handleFlowFetch, notify]);

  const onEditNumber = useCallback(() => {
    ampli.phoneOptionsMenuOptionSetupClicked();
    showEditNumber(number);
  }, [showEditNumber, number]);

  const onNewFlow = useCallback(() => {
    ampli.phoneOptionsMenuOptionCreateFlowClicked();
    showNewFlow(number);
  }, [showNewFlow, number]);

  const isOutboundCallerID = carrier === 'other' && number_type === 'fixed';
  const isOutboundVerified =
    outgoing_functionality !== false && outbound_verified !== false;

  const shouldCollapse =
    outgoing_functionality === false && incoming_functionality === false;

  /*
   * NumberTableItem is wrapped in withFlows & withPhoneNumbers as opposed to NumberTableFlows
   * because Table will unmount the `expandableContent` component when it's not expanded,
   * thus the fetched flow information would go with it.
   */

  return (
    <Table.Tr
      rowId={e164_format}
      {...(!isOutboundCallerID && {
        expandableContent: (
          <NumberTableFlows
            handleFlowFetch={handleFlowFetch}
            number={number}
            flows={flows}
          />
        ),
      })}
    >
      <Table.Td className="flags">
        <div className={styles.number}>
          <span className={classnames(styles.flag, 'flag', countryCode)} />
          <span className={styles.prettyNumber}>{prettyNumber}</span>
          {record_outbound_calls && (
            <Tooltip
              content={`All outbound calls made with phone number ${prettyNumber} are recorded`}
              dark
              wrap="span"
            >
              <Icon name="rec" className={styles.record} />
            </Tooltip>
          )}
          {isOutboundCallerID && (
            <Tooltip
              content={
                <>
                  You can set this number as your organization’s default caller
                  ID.
                  <br />
                  <br />
                  {
                    <i>
                      Note that you can’t create flows for this number, as it
                      functions as a caller ID only.
                    </i>
                  }
                </>
              }
              dark
            >
              <Badge className={styles.badge} size="small" variant="info">
                Caller ID
              </Badge>
            </Tooltip>
          )}
          {!isOutboundVerified && (
            <Tooltip
              content={
                <>
                  We weren’t able to verify {prettyNumber}.
                  <br />
                  Please contact Dixa’s Support Team for further assistance.
                </>
              }
              dark
            >
              <Badge className={styles.badge} size="small" variant="error">
                Outbound not verified
              </Badge>
            </Tooltip>
          )}
          {organization_default && (
            <Tooltip
              content={`You’ve set ${prettyNumber} as your organization's default caller ID.`}
              dark
            >
              <Badge className={styles.badge} size="small" variant="info">
                Default caller ID
              </Badge>
            </Tooltip>
          )}
          {incoming_functionality === false && (
            <Tooltip
              content={
                <>
                  We weren’t able to verify {prettyNumber}.
                  <br />
                  Please contact Dixa’s Support Team for further assistance.
                </>
              }
              dark
            >
              <Badge
                className={classnames(styles.badge, {
                  [styles.badgeCollapsed]: shouldCollapse,
                })}
                size="small"
                variant="error"
              >
                Inbound not verified
              </Badge>
            </Tooltip>
          )}
        </div>
      </Table.Td>
      <Table.Td>{name ?? 'Unnamed'}</Table.Td>
      <Table.Td>
        {hasChannelSetupAccess && (
          <DropdownButton
            className={styles.dropdown}
            icon="dots-h"
            fixed
            buttonProps={{ variant: 'tertiary' }}
            noArrow
            onToggle={onToggleMenu}
            dropdown={
              <ButtonList>
                {!isOutboundCallerID && (
                  <>
                    <ButtonList.Item onClick={onNewFlow}>
                      Create flow
                    </ButtonList.Item>
                    <ButtonList.Item onClick={onEditFlow}>Edit</ButtonList.Item>
                  </>
                )}
                <ButtonList.Item onClick={onEditNumber}>
                  Settings
                </ButtonList.Item>
                <ButtonList.Divider />
                <ButtonList.Item
                  isDisabled={!isOutboundVerified}
                  onClick={onOutbound}
                >
                  Set as default caller ID
                </ButtonList.Item>
                <ButtonList.Item onClick={onRelease}>
                  Remove number
                </ButtonList.Item>
              </ButtonList>
            }
          />
        )}
      </Table.Td>
    </Table.Tr>
  );
}

export default withFlows<FlowCallActionT, PropsT>(NumberTableItem);
